/** How the keyword table reads and orders a keyword row (S3, keyword tab).
 *
 *  Pure functions over `KeywordRow`, so the table, the analyze-all loop and
 *  the tests share one definition. */

import type { KeywordRow, RankPoint } from '@/api/client'
import { opportunityScore, opportunityTier } from '@/lib/opportunity'

export type KeywordSort = 'opportunity' | 'rank' | 'popularity' | 'difficulty' | 'updated' | 'term'
export type SortDir = 'asc' | 'desc'

export const SORT_OPTIONS: { value: KeywordSort; label: string }[] = [
  { value: 'opportunity', label: '기회' },
  { value: 'rank', label: '순위' },
  { value: 'popularity', label: '인기도' },
  { value: 'difficulty', label: '난이도' },
  { value: 'updated', label: '갱신일' },
  { value: 'term', label: '키워드' },
]

export function asSort(v: string): KeywordSort {
  return SORT_OPTIONS.some((o) => o.value === v) ? (v as KeywordSort) : 'opportunity'
}

/** A keyword the manual "미분석 분석" has work for: tracked, and its latest
 *  snapshot has neither popularity nor difficulty. The tracker refreshes any
 *  keyword that already has a snapshot on its own schedule, so the button only
 *  fills in the never-analyzed ones. */
export function needsAnalysis(k: KeywordRow): boolean {
  return k.is_tracking && (k.latest?.popularity ?? 0) === 0 && (k.latest?.difficulty ?? 0) === 0
}

const TIER_WEIGHT = { golden: 4, hidden_gem: 3, competitive: 2, low_value: 1 } as const

/** Higher is better: 최우선 > 틈새 > 치열 > 낮음; not measured has none. */
export function opportunityWeight(popularity: number, difficulty: number): number | null {
  const tier = opportunityTier(popularity, difficulty)
  return tier === 'unscored' ? null : TIER_WEIGHT[tier]
}

export interface Trend {
  status: 'up' | 'down' | 'same' | 'lost'
  /** Places moved, always ≥ 0. */
  delta: number
  from: number
  to: number
}

/** The move across the visible history: first ranked point against the last.
 *  A lower rank number is better, so a smaller `to` is "up". A keyword whose
 *  latest point is out of the ranking has left it, whatever it did before
 *  ("이탈", not the ▲4 its earlier points would give). Otherwise fewer than
 *  two ranked points have no trend. */
export function keywordTrend(history?: RankPoint[]): Trend | null {
  const all = history ?? []
  const ranked = all.filter((p) => p.rank > 0)
  if (all.length > 0 && all[all.length - 1].rank === 0 && ranked.length > 0) {
    const from = ranked[ranked.length - 1].rank
    return { status: 'lost', delta: 0, from, to: 0 }
  }
  if (ranked.length < 2) return null
  const from = ranked[0].rank
  const to = ranked[ranked.length - 1].rank
  return { status: to < from ? 'up' : to > from ? 'down' : 'same', delta: Math.abs(from - to), from, to }
}

/** The rank to show: the store's own order when it was read, else the Search
 *  API's. 0 means not ranked. */
export function shownRank(k: KeywordRow): number {
  return k.store ? k.store.rank : (k.latest?.rank ?? 0)
}

function sortValue(k: KeywordRow, key: KeywordSort): string | number | null {
  const pop = k.latest?.popularity ?? 0
  const diff = k.latest?.difficulty ?? 0
  switch (key) {
    case 'term':
      return (k.term ?? '').toLowerCase()
    // 0 means "not ranked": it goes last whichever way the list is turned.
    case 'rank': {
      const rank = shownRank(k)
      return rank > 0 ? rank : null
    }
    case 'popularity':
      return k.latest?.popularity ?? null
    case 'difficulty':
      return k.latest?.difficulty ?? null
    case 'opportunity': {
      const w = opportunityWeight(pop, diff)
      // Inside a tier the weighted score orders the rows.
      return w === null ? null : w * 1000 + opportunityScore(pop, diff)
    }
    case 'updated':
      return k.latest?.snapshot_date ?? null
  }
}

/** A sorted copy. Rows without a value for the key always come last. */
export function sortKeywords(rows: KeywordRow[], key: KeywordSort, dir: SortDir): KeywordRow[] {
  const mul = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key)
    const bv = sortValue(b, key)
    if (av === null && bv === null) return 0
    if (av === null) return 1
    if (bv === null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul
    return String(av).localeCompare(String(bv)) * mul
  })
}

/** Keywords grouped by storefront code, lower case. */
export function countsByCountry(rows: KeywordRow[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const k of rows) {
    const c = (k.country || 'us').toLowerCase()
    out.set(c, (out.get(c) ?? 0) + 1)
  }
  return new Map([...out.entries()].sort(([a], [b]) => a.localeCompare(b)))
}
